<template>
  <v-expand-transition>
    <div v-show="obj.isOpen">
      <v-form v-model="isValid" ref="form">
        <v-card-text class="pa-0">
          <!-- 土量 -->
          <v-card-actions class="pr-0" v-show="config.useSoilVolumeCalculation">
            {{ $t("Soilvolume") }}
            <v-btn height="20px" icon v-if="isEditOnProgress" @click="calculateFlatGroundVolume(obj)" class="ml-2"
              title="ボリュームを計算する" :loading="obj.loadingVolume">
              <v-icon> mdi-play-circle-outline </v-icon>
            </v-btn>
          </v-card-actions>
          <v-expand-transition>
            <v-table density="compact" class="ml-4 volume-table">
              <tbody>
                <tr>
                  <td>
                    {{ $t("Cut") }}
                  </td>
                  <td>
                    <v-text-field variant="underlined" hide-details density="compact" type="number" min="0" step="0.1"
                      :suffix="volumeLabel" readonly :model-value="toDisplayVolume(obj.cutVolume ?? 0, 3).value" />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{ $t("FILL") }}
                  </td>
                  <td>
                    <v-text-field variant="underlined" hide-details density="compact" type="number" min="0" step="0.1"
                      :suffix="volumeLabel" readonly :model-value="toDisplayVolume(obj.fillVolume ?? 0, 3).value" />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-expand-transition>
          <v-card-actions class="pr-0" v-show="config.useSurfaceAreaCalculation">
            {{ $t("SurfaceArea") }}
            <v-btn height="20px" icon v-if="isEditOnProgress" @click="calculateSurfaceArea(obj)" class="ml-3"
              title="ボリュームを計算する">
              <v-icon> mdi-play-circle-outline </v-icon>
            </v-btn>
          </v-card-actions>
          <v-expand-transition>
            <v-table density="compact" class="ml-4 volume-table">
              <tbody>
                <tr>
                  <td>
                    {{ $t("AREA") }}
                  </td>
                  <td>
                    <v-text-field variant="underlined" hide-details density="compact" type="number" min="0" step="0.1"
                      :suffix="areaLabel" readonly :model-value="toDisplayArea(obj.surfaceArea ?? 0, 3).value" />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-expand-transition>
          <template v-if="isEditOnProgress">
            <!-- 平場 -->
            <v-card-actions class="pr-0">
              {{ $t("FLATGROUND_PARAMETERS") }}
            </v-card-actions>
            <v-table density="compact" class="ml-4">
              <tbody>
                <!-- 法面あり -->
                <tr>
                  <td>
                    {{ $t("With_Slope") }}
                  </td>
                  <td>
                    <v-checkbox hide-details density="compact" class="ma-0" :disabled="!isEditOnProgress"
                      @update:model-value="updateSettings" v-model="obj.surfaceSettings.withSlope"></v-checkbox>
                  </td>
                </tr>
                <!-- 法面ピッチ -->
                <tr>
                  <td>
                    {{ $t("FLATGROUND_INTERVAL") }}
                  </td>
                  <td>
                    <DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
                      :disabled="!isEditOnProgress || !withSlope" @blur="onBlur"
                      v-model="obj.surfaceSettings.interpolationPitch" :min-value="0.001" :max-value="99.999"
                      :decimal-length="3" />
                  </td>
                </tr>
                <tr>
                  <td>
                    {{ $t("FLATGROUND_HEIGHT") }}
                  </td>
                  <td>
                    <DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
                      :disabled="!isEditOnProgress" @blur="setFlatGroundPointsUniformHeight()" :decimal-length="3"
                      v-model="obj.maxHeight" />
                  </td>
                </tr>
              </tbody>
            </v-table>
            <!-- 摺り付け設定 -->
            <v-card-actions class="section ml-4 pr-2" @click="toggle('interpolation')" style="cursor: pointer;">
              {{ $t("RUBBLING_SETTINGS") }} <v-spacer></v-spacer>
              <v-icon size="small">{{ expanded.interpolation ? 'mdi-chevron-up' : 'mdi-chevron-down'
              }}</v-icon>
            </v-card-actions>
            <v-expand-transition>
              <div v-show="expanded.interpolation" class="ml-4">
                <v-table density="compact">
                  <tbody>
                    <!-- 摺り付け点群 -->
                    <tr>
                      <td>
                        {{ $t("ASSOCIATED_TERRAIN_TYPE") }}
                      </td>
                      <td>
                        <v-select variant="plain" v-model="associatedTerrain.terrainType" :items="possibleTerrainTypes"
                          :item-title="item => item?.label" :item-value="item => item?.value" density="compact"
                          hide-details :disabled="!isEditOnProgress" class="ma-0"
                          @update:model-value="updateAssociatedTerrainType()">
                        </v-select>
                      </td>
                    </tr>
                    <tr v-if="associatedTerrain.terrainType === 'pointcloud'">
                      <td>{{ $t("ASSOCIATED_POINT_CLOUD") }}</td>
                      <td>
                        <v-select variant="plain" v-model="associatedTerrain.id" :items="pointClouds"
                          :item-title="item => item.getName()" :item-value="item => item.getAssetIdNum()"
                          density="compact" hide-details :disabled="!isEditOnProgress" class="ma-0"
                          @update:model-value="updateAssociatedTerrain()">
                        </v-select>
                      </td>
                    </tr>
                    <tr v-if="associatedTerrain.terrainType === 'design'">
                      <td>{{ $t("ASSOCIATED_DESIGN_DATA") }}</td>
                      <td>
                        <v-select variant="plain" v-model="associatedTerrain.id" :items="designs"
                          :item-title="item => item.getName()" :item-value="item => item.getAssetIdNum()"
                          density="compact" hide-details :disabled="!isEditOnProgress" class="ma-0"
                          @update:model-value="updateAssociatedTerrain()">
                        </v-select>
                      </td>
                    </tr>
                    <tr v-if="associatedTerrain.terrainType === 'reference-topology'">
                      <td>{{ $t("ASSOCIATED_REFERENCE_TOPOLOGY") }}</td>
                      <td>
                        <v-select variant="plain" v-model="associatedTerrain.id" :items="referenceTopologies"
                          :item-title="item => item.getName()" :item-value="item => item.getAssetIdNum()"
                          density="compact" hide-details :disabled="!isEditOnProgress" class="ma-0"
                          @update:model-value="updateAssociatedTerrain()">
                        </v-select>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </div>
            </v-expand-transition>
            <!-- 切土設定 -->
            <v-card-actions class="section pr-2" @click="toggle('cut')">
              {{ $t("Cutsetting") }}
              <v-spacer></v-spacer>
              <v-icon size="small">{{ expanded.cut ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-card-actions>
            <v-expand-transition>
              <div v-show="expanded.cut" class="ml-4">
                <v-table density="compact">
                  <tbody>
                    <!-- 法面高さ -->
                    <tr>
                      <td>
                        {{ $t("Slopeheight") }}
                      </td>
                      <td>
                        <DistanceSettingInput variant="underlined" hide-details density="compact" :min-value="0.001"
                          :decimal-length="3" :step="0.001" :max-value="99.999"
                          :disabled="!isEditOnProgress || (!obj.cutSettings.generateShelf && !withSlope)" @blur="onBlur"
                          v-model="obj.cutSettings.height" />
                      </td>
                    </tr>
                    <!-- 法面勾配 -->
                    <tr>
                      <td>
                        {{ $t("Slopegradient") }}
                      </td>
                      <td>
                        <SlopeSettingInput :suffix-to-display="getSuffix(cutSettingSlopeUnit.slopeGradient)"
                          :is-editable="isEditOnProgress && withSlope" :slope-value="obj.cutSettings.moundSlope"
                          :step="0.01" @blur="onBlur" v-model.number="obj.cutSettings.moundSlope"
                          :max-value="cutSettingSlopeUnit.slopeGradient === 'percent' ? 9999.99 : 999.99"
                          :min-value="0.01" :decimal-length="2" :un-valid-zero="false">
                        </SlopeSettingInput>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
                <!-- 小段設定 -->
                <v-card-actions class="section pr-2" @click="toggle('cutBerm')">
                  {{ $t("BERM_SETTINGS") }}
                  <v-spacer></v-spacer>
                  <v-icon size="small">{{ expanded.cutBerm ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-card-actions>
                <v-expand-transition>
                  <div v-show="expanded.cutBerm">
                    <v-table density="compact">
                      <tbody>
                        <!-- 小段あり -->
                        <tr>
                          <td>
                            {{ $t("Thereisberm") }}
                          </td>
                          <td>
                            <v-checkbox v-model="obj.cutSettings.generateShelf" hide-details density="compact"
                              class="ma-0" :disabled="!isEditOnProgress || !withSlope"
                              @update:model-value="updateSettings()"></v-checkbox>
                          </td>
                        </tr>
                        <!-- 小段幅 -->
                        <tr>
                          <td>
                            {{ $t("Bermwidth") }}
                          </td>
                          <td>
                            <DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
                              :min-value="0.001" :max-value="99.999" :decimal-length="3"
                              :disabled="!isEditOnProgress || !obj.cutSettings.generateShelf || !withSlope"
                              @blur="onBlur" v-model="obj.cutSettings.shelfWidth" />
                          </td>
                        </tr>
                        <!-- 小段勾配 -->
                        <tr>
                          <td>
                            {{ $t("Bermgradient") }}
                          </td>
                          <td>
                            <SlopeSettingInput :suffix-to-display="getSuffix(cutSettingSlopeUnit.bermGradient)"
                              :is-editable="isEditOnProgress && obj.cutSettings.generateShelf && withSlope"
                              :slope-value="obj.cutSettings.shelfSlope" :step="0.01" @blur="onBlur"
                              v-model.number="obj.cutSettings.shelfSlope"
                              :max-value="cutSettingSlopeUnit.bermGradient === 'percent' ? 9999.99 : 999.99"
                              :min-value="cutSettingSlopeUnit.bermGradient === 'percent' ? 0 : 0.01" :decimal-length="2"
                              :un-valid-zero="cutSettingSlopeUnit.bermGradient === 'percent'">
                            </SlopeSettingInput>
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </div>
                </v-expand-transition>
              </div>
            </v-expand-transition>
            <!-- 盛土設定 -->
            <v-card-actions class="section pr-2" @click="toggle('fill')">
              {{ $t("Fillsetting") }}
              <v-spacer></v-spacer>
              <v-icon size="small">{{ expanded.fill ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-card-actions>
            <v-expand-transition>
              <div v-show="expanded.fill" class="ml-4">
                <v-table density="compact">
                  <tbody>
                    <!-- 法面高さ -->
                    <tr>
                      <td>
                        {{ $t("Slopeheight") }}
                      </td>
                      <td>
                        <DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
                          :min-value="0.001" :max-value="99.999" :decimal-length="3"
                          :disabled="!isEditOnProgress || (!obj.fillSettings.generateShelf && !withSlope)"
                          @blur="onBlur" v-model="obj.fillSettings.height" />
                      </td>
                    </tr>
                    <!-- 法面勾配 -->
                    <tr>
                      <td>
                        {{ $t("Slopegradient") }}
                      </td>
                      <td>
                        <SlopeSettingInput :suffix-to-display="getSuffix(fillSettingSlopeUnit.slopeGradient)"
                          :is-editable="isEditOnProgress && withSlope" :slope-value="obj.fillSettings.moundSlope"
                          :step="0.01" @blur="onBlur" v-model.number="obj.fillSettings.moundSlope"
                          :max-value="fillSettingSlopeUnit.slopeGradient === 'percent' ? 9999.99 : 999.99"
                          :min-value="0.01" :decimal-length="2" :un-valid-zero="false">
                        </SlopeSettingInput>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
                <!-- 小段設定 -->
                <v-card-actions class="section pr-2" @click="toggle('fillBerm')">
                  {{ $t("BERM_SETTINGS") }}
                  <v-spacer></v-spacer>
                  <v-icon size="small">{{ expanded.fillBerm ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-card-actions>
                <v-expand-transition>
                  <div v-show="expanded.fillBerm">
                    <v-table density="compact">
                      <tbody>
                        <!-- 小段あり -->
                        <tr>
                          <td>
                            {{ $t("Thereisberm") }}
                          </td>
                          <td>
                            <v-checkbox v-model="obj.fillSettings.generateShelf" hide-details density="compact"
                              class="ma-0" :disabled="!isEditOnProgress || !withSlope"
                              @update:model-value="updateSettings()"></v-checkbox>
                          </td>
                        </tr>
                        <!-- 小段幅 -->
                        <tr>
                          <td>
                            {{ $t("Bermwidth") }}
                          </td>
                          <td>
                            <DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
                              :min-value="0.001" :max-value="99.999" :decimal-length="3"
                              :disabled="!(isEditOnProgress && obj.fillSettings.generateShelf && withSlope)"
                              @blur="onBlur" v-model="obj.fillSettings.shelfWidth" />
                          </td>
                        </tr>
                        <!-- 小段勾配 -->
                        <tr>
                          <td>
                            {{ $t("Bermgradient") }}
                          </td>
                          <td>
                            <SlopeSettingInput :suffix-to-display="getSuffix(fillSettingSlopeUnit.bermGradient)"
                              :is-editable="isEditOnProgress && obj.fillSettings.generateShelf && withSlope"
                              :slope-value="obj.fillSettings.shelfSlope" :step="0.01" @blur="onBlur"
                              v-model.number="obj.fillSettings.shelfSlope"
                              :max-value="fillSettingSlopeUnit.bermGradient === 'percent' ? 9999.99 : 999.99"
                              :min-value="fillSettingSlopeUnit.bermGradient === 'percent' ? 0 : 0.01"
                              :decimal-length="2" :un-valid-zero="fillSettingSlopeUnit.bermGradient === 'percent'">
                            </SlopeSettingInput>
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </div>
                </v-expand-transition>
              </div>
            </v-expand-transition>
            <!-- 色設定 -->
            <v-card-actions class="section pr-2" @click="toggle('color')">
              {{ $t("COLOR_SETTINGS") }}
              <v-spacer></v-spacer>
              <v-icon size="small">{{ expanded.color ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-card-actions>
            <v-expand-transition>
              <div v-show="expanded.color" class="ml-4">
                <v-card-actions class="py-0 mr-3">
                  {{ $t("OPACITY") }}
                  <v-slider v-model="obj.transparency" :disabled="!isEditOnProgress" hide-details max="1" step="0.01"
                    class="ml-1" color="primary" track-size="0.5" thumb-size="14"></v-slider>
                </v-card-actions>
                <v-table density="compact">
                  <tbody>
                    <tr>
                      <td>
                        {{ $t("FLAT_GROUND_SURFACE_COLOR") }}
                      </td>
                      <td>
                        <v-menu>
                          <template v-slot:activator="{ props }">
                            <v-avatar :style="flatGroundColorStyle" :color="obj.surfaceSettings.color" size="20px"
                              v-bind="props">
                            </v-avatar>
                          </template>
                          <v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
                            v-model="obj.surfaceSettings.color">
                          </v-color-picker>
                        </v-menu>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $t("CUT_SLOPE_COLOR") }}
                      </td>
                      <td>
                        <v-menu :disabled="!isEditOnProgress || !withSlope">
                          <template v-slot:activator="{ props }">
                            <v-avatar :style="flatGroundColorStyle" :color="obj.cutSettings.color" size="20px"
                              v-bind="props"> </v-avatar>
                          </template>
                          <v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
                            v-model="obj.cutSettings.color"></v-color-picker>
                        </v-menu>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $t("FILL_SLOPE_COLOR") }}
                      </td>
                      <td>
                        <v-menu :disabled="!isEditOnProgress || !withSlope">
                          <template v-slot:activator="{ props }">
                            <v-avatar :style="flatGroundColorStyle" :color="obj.fillSettings.color" size="20px"
                              v-bind="props"> </v-avatar>
                          </template>
                          <v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
                            v-model="obj.fillSettings.color"></v-color-picker>
                        </v-menu>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </div>
            </v-expand-transition>
          </template>
        </v-card-text>
      </v-form>
    </div>
  </v-expand-transition>
</template>

<script lang="ts">
import "@/assets/icons/style.css";
import config from "@/config.json";
import { TerrainType } from "@/constant.js";
import * as cesiumCommon from "@/utils/cesium-common";
import { notifySuccess, notifyWarning } from "@/utils/notificationUtils";
import { mapMutations, useStore } from "vuex";
import { UnitTypes } from "../../../constant";
import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "../../../utils/googleAnalytics.js";
import SlopeSettingInput from "../SlopeSettingInput.vue";
import NumericSettingInput from "../NumericSettingInput.vue";
import DistanceSettingInput from "../DistanceSettingInput.vue";
import { useObjectPanel } from "./composables/useObjectPanel";
import { useUnitDisplay } from "@/composables/useUnitDisplay";

export default {
	setup(props) {
		const { obj, isValid, form } = useObjectPanel(props.objId);
		const { toDisplayVolume, toDisplayArea, volumeLabel, areaLabel } =
			useUnitDisplay();
		return {
			store: useStore(),
			obj,
			isValid,
			form,
			toDisplayVolume,
			toDisplayArea,
			volumeLabel,
			areaLabel,
		};
	},
	name: "FlatGroundPanel",
	emits: ["update:valid"],
	components: {
		SlopeSettingInput,
		NumericSettingInput,
		DistanceSettingInput,
	},
	props: {
		objId: String,
		rules: Object,
	},
	computed: {
		isEditOnProgress() {
			return this.store.state.object.objectUnderEdit?.id === this.obj.id;
		},
		possibleTerrainTypes() {
			const terrainTypes = [];
			if (this.pointClouds.length > 0) {
				terrainTypes.push({
					label: this.$t("PointCloud"),
					value: TerrainType.POINT_CLOUD,
				});
			}
			if (this.designs.length > 0) {
				terrainTypes.push({
					label: this.$t("DesignData"),
					value: TerrainType.DESIGN_DATA,
				});
			}
			if (this.referenceTopologies.length > 0) {
				terrainTypes.push({
					label: this.$t("ReferenceTopology"),
					value: TerrainType.REFERENCE_TOPOLOGY,
				});
			}
			return terrainTypes;
		},
		pointClouds() {
			return this.store.getters.terrainList
				.filter((item) => item.getType() === TerrainType.POINT_CLOUD)
				.filter((item) => item.getIsAssetUploaded() === "DONE");
		},
		designs() {
			return this.store.getters.terrainList
				.filter((item) => item.getType() === TerrainType.DESIGN_DATA)
				.filter((item) => item.getIsAssetUploaded() === "DONE");
		},
		referenceTopologies() {
			return this.store.getters.terrainList.filter(
				(item) => item.getType() === TerrainType.REFERENCE_TOPOLOGY,
			);
		},
		/**
		 * 水平地形色のスタイル
		 */
		flatGroundColorStyle() {
			// 編集不可のときに適用するCSS
			if (!this.isEditOnProgress) {
				return {
					opacity: 0.5,
					"pointer-events": "none",
				};
			}
			// 編集可のときに適用するCSS
			return {
				opacity: 1.0,
			};
		},
		withSlope: {
			get() {
				return this.obj.surfaceSettings.withSlope;
			},
			set(value) {
				this.obj.surfaceSettings.withSlope = value;
			},
		},
		cutSettingSlopeUnit() {
			return this.store.state.cutSettingSlopeUnit;
		},
		fillSettingSlopeUnit() {
			return this.store.state.fillSettingSlopeUnit;
		},
	},
	data() {
		return {
			config,
			expanded: {
				interpolation: false,
				cut: false,
				cutBerm: false,
				fill: false,
				fillBerm: false,
				color: false,
			},
			associatedTerrain: {
				id: null,
				terrainType: null,
			},
		};
	},
	watch: {
		isValid(newVal) {
			this.$emit("update:valid", newVal);
		},
	},
	methods: {
		...mapMutations(["set_snackbar", "reset_snackbars"]),
		toggle(section) {
			this.expanded[section] = !this.expanded[section];
		},
		getSuffix(unitType) {
			const unit = UnitTypes.find((type) => type.value === unitType);
			return unit.symbol;
		},
		async onBlur() {
			await this.updateSettings();
		},
		async updateSettings() {
			this.store.dispatch("startObjectUpdating");
			try {
				const updateInViewer = this.obj.isShow;
				await this.$nextTick();
				const validation = await this.$refs.form.validate();
				if (!validation.valid) return;
				await this.obj.update(this.obj, updateInViewer);
			} finally {
				this.$store.dispatch("endObjectUpdating");
			}
		},
		async updateAssociatedTerrain() {
			this.store.dispatch("startObjectUpdating");
			this.reset_snackbars();
			if (!this.associatedTerrain?.id) {
				notifyWarning(this, this.$t("SELECT_ASSOCIATED_TERRAIN"));
				return;
			}
			let hasUpdateError = false;
			try {
				const terrain = this.store.getters.terrainList.find(
					(terrain) => terrain.getAssetIdNum() === this.associatedTerrain.id,
				);
				if (!terrain) {
					throw new Error("Terrain not found");
				}
				await cesiumCommon.setAssociatedTerrainVisible(terrain);
				await this.obj.updateAssociatedTerrain(
					this.associatedTerrain.id,
					this.associatedTerrain.terrainType,
				);
				// データ削除区分
				// this.$emit("road-panel-data", true);
			} catch {
				hasUpdateError = true;
				this.associatedTerrain.id = this.obj.associatedTerrain.id;
				this.associatedTerrain.terrainType =
					this.obj.associatedTerrain.terrainType;
			} finally {
				this.$store.dispatch("endObjectUpdating");
			}
			// 変更内容を保存
		},
		async calculateFlatGroundVolume(obj) {
			// Google Analyticsに連携
			addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_OBJECT_VOLUME_CALC);

			obj.loadingVolume = true;
			try {
				const res =
					await this.store.getters.viewer.volumeCalculationForFlatGround(
						obj.id,
					);
				obj.cutVolume = res.cutVolume.toFixed(3);
				obj.fillVolume = res.fillVolume.toFixed(3);
			} catch (error) {
				console.error(error.message);
			} finally {
				obj.loadingVolume = false;
			}
			this.$forceUpdate();
		},
		calculateSurfaceArea(obj) {
			// Google Analyticsに連携
			addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_OBJECT_AREA_CALC);
			const res = this.store.state.viewer.calculateSurfaceAreaOfFlatGround(
				obj.id,
			);
			obj.surfaceArea = res.toFixed(3);
			console.log(obj.surfaceArea);
		},
		async setFlatGroundPointsUniformHeight() {
			if (this.obj.id === null || !this.isValid) return;

			this.store.dispatch("startObjectUpdating");
			try {
				await this.store.getters.viewer.setFlatGroundPointsUniformHeight(
					this.obj.id,
					Number.parseFloat(this.obj.maxHeight),
				);
			} finally {
				this.$store.dispatch("endObjectUpdating");
			}
		},
		updateAssociatedTerrainType() {
			this.associatedTerrain.id = null;
			this.reset_snackbars();
			// Show instruction message until user completes the operation (timeout: -1 for manual dismissal)
			notifySuccess(
				{ $store: this.store, $t: this.$t },
				this.$t("SELECT_ASSOCIATED_TERRAIN"),
				-1,
			);
		},
	},
	mounted() {
		this.associatedTerrain.id = this.obj.associatedTerrain.id;
		this.associatedTerrain.terrainType = this.obj.associatedTerrain.terrainType;
	},
};
</script>

<style scoped>
tr td {
  padding: 0 8px !important;
}

tr td:first-child {
  width: 115px;
}

.volume-table tr td:first-child {
  min-width: 80px;
  width: 80px;
}

.v-card-actions.section:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.08);
}

.v-table {
  background-color: transparent;
}

.v-table :deep(.v-select .v-select__selection-text) {
  white-space: normal;
  font-size: 14px;
}
</style>
